import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Crown, ShieldCheck, MessageSquare, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { genUserName } from '@/lib/genUserName';
import { crewAccent } from '@/lib/crewHelpers';
import { CREW_ROLE_META, type CrewRole, type SkateCrew } from '@/lib/types';
import { cn } from '@/lib/utils';

/**
 * A single leader ("Team Leader" for the founder, "Captain" for admins).
 * Big, personable, links to the profile + a quick message action.
 */
function LeaderCard({
  pubkey,
  role,
  accentHex,
  confirmed,
  skateRoles,
}: {
  pubkey: string;
  role: 'leader' | 'captain';
  accentHex: string;
  confirmed?: boolean;
  skateRoles?: CrewRole[];
}) {
  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;
  const name = meta?.name ?? genUserName(pubkey);
  const pic = meta?.picture;
  const npub = nip19.npubEncode(pubkey);
  const { user } = useCurrentUser();
  const isSelf = user?.pubkey === pubkey;

  const isLeader = role === 'leader';
  const RoleIcon = isLeader ? Crown : ShieldCheck;
  const roleLabel = isLeader ? 'Team Leader' : 'Captain';

  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 overflow-hidden rounded-2xl border p-3 transition-all',
        isLeader ? 'border-transparent' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]',
      )}
      style={
        isLeader
          ? {
              background: `linear-gradient(135deg, ${accentHex}1f, transparent 70%)`,
              borderColor: `${accentHex}55`,
            }
          : undefined
      }
    >
      <Link to={`/${npub}`} className="relative shrink-0">
        <div
          className="rounded-2xl p-[2px]"
          style={{ background: isLeader ? `linear-gradient(135deg, ${accentHex}, ${accentHex}55)` : 'transparent' }}
        >
          <Avatar className={cn('rounded-[calc(1rem-2px)]', isLeader ? 'h-14 w-14' : 'h-11 w-11')}>
            <AvatarImage src={pic} alt={name} className="object-cover" />
            <AvatarFallback
              className="rounded-[calc(1rem-2px)] font-black"
              style={{ color: accentHex, background: `${accentHex}1f` }}
            >
              {name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
      </Link>

      <div className="min-w-0 flex-1">
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider"
          style={{ color: accentHex, background: `${accentHex}1f` }}
        >
          <RoleIcon className="h-3 w-3" />
          {roleLabel}
        </span>
        <Link to={`/${npub}`} className="mt-1 block">
          <p className={cn('truncate font-black text-white', isLeader ? 'text-lg' : 'text-sm')}>
            {name}
          </p>
        </Link>
        {confirmed && !isLeader && (
          <p className="flex items-center gap-1 text-[11px] font-bold text-green-500">
            <Check className="h-3 w-3" /> Verified member
          </p>
        )}
        {skateRoles && skateRoles.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {skateRoles.map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-0.5 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-gray-200"
              >
                <span className="leading-none">{CREW_ROLE_META[r].emoji}</span> {CREW_ROLE_META[r].label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Quick DM (not for yourself) */}
      {!isSelf && (
        <Link
          to={`/messages/${npub}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 text-gray-400 transition-colors hover:border-white/20 hover:text-white"
          title={`Message ${name}`}
        >
          <MessageSquare className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

/**
 * "The Leadership" — the human hierarchy of a crew.
 *
 * - **Team Leader**: the founder (author of the crew event). The one who
 *   ultimately controls the roster on Nostr.
 * - **Captains**: delegated admins who help run the crew (invite, moderate).
 *
 * This makes the crew read like a real team, and lays the groundwork for
 * dedicated coordinator roles (MLS / local coordinator) that will sync with
 * Lab profiles.
 */
export function CrewLeadership({
  crew,
  confirmedAdmins,
  rolesByPubkey,
}: {
  crew: SkateCrew;
  /** Admin pubkeys whose membership is confirmed (for the verified tick). */
  confirmedAdmins?: Set<string>;
  /** Skate roles per member (pubkey → roles) for role chips. */
  rolesByPubkey?: Map<string, CrewRole[]>;
}) {
  const accent = crewAccent(crew);
  // Admins are always a subset of members; founder is never listed as an admin card.
  const captains = crew.admins.filter((pk) => pk !== crew.pubkey);

  return (
    <Card className="border-2 bg-black/90" style={{ borderColor: `${accent.hex}40` }}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-black uppercase text-white">
          <Crown className="h-5 w-5" style={{ color: accent.hex }} />
          Leadership
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <LeaderCard
          pubkey={crew.pubkey}
          role="leader"
          accentHex={accent.hex}
          skateRoles={rolesByPubkey?.get(crew.pubkey)}
        />

        {captains.length > 0 && (
          <>
            <p className="px-1 pt-1 text-[11px] font-black uppercase tracking-widest text-gray-500">
              {captains.length} Captain{captains.length === 1 ? '' : 's'}
            </p>
            {captains.map((pk) => (
              <LeaderCard
                key={pk}
                pubkey={pk}
                role="captain"
                accentHex={accent.hex}
                confirmed={confirmedAdmins?.has(pk)}
                skateRoles={rolesByPubkey?.get(pk)}
              />
            ))}
          </>
        )}

        <p className="pt-1 text-[11px] leading-relaxed text-gray-500">
          The Team Leader runs the roster; Captains help invite, host jams and keep the crew tidy.
        </p>
      </CardContent>
    </Card>
  );
}
